import { Component, computed, inject, input } from '@angular/core';
import { InnogyComponentRendering } from '@core/jss-models';
import { ContentSingleColumnTextSectionComponent } from '@content/ui/content-single-column-text';
import { FieldService, GenericLinkDirective } from '@core/jss-utils';
import { Store } from '@ngrx/store';
import { getAppStoreButton, getGooglePlayButton } from '@core/navigation-store';
import { AppStoreButtonsComponent } from '@core/navigation-ui';
import { CrntButtonAlt } from '@essent/current-web-angular';
import { TextField } from '@core/jss-proxy';
import { LinkField } from '@sitecore-jss/sitecore-jss-angular';
import {
  computedContentSectionId,
  computedContentSectionTheme,
  ContentSectionComponent,
} from '@core/content-section';
import { TrackLinkDirective } from '@analytics/events';
import { NgTemplateOutlet } from '@angular/common';

enum TextSectionTemplateNames {
  DEFAULT = 'TextDefault',
  WITH_APP_STORE_BUTTONS = 'TextWithAppStoreButtons',
  WITH_CALL_TO_ACTION_BUTTON = 'TextWithCallToActionButton',
}

interface TextSectionComponentRendering extends InnogyComponentRendering {
  heading: TextField;
  richText: TextField;
  callToActionButtonLink?: LinkField;
}

@Component({
  selector: 'wl-content-section-text-container',
  standalone: true,
  templateUrl: './content-section-text-container.component.html',
  imports: [
    ContentSingleColumnTextSectionComponent,
    AppStoreButtonsComponent,
    CrntButtonAlt,
    GenericLinkDirective,
    ContentSectionComponent,
    TrackLinkDirective,
    NgTemplateOutlet,
  ],
})
export class ContentSectionTextContainerComponent {
  readonly rendering = input.required<TextSectionComponentRendering>();

  readonly id = computedContentSectionId(this.rendering);
  readonly theme = computedContentSectionTheme(this.rendering);

  readonly #fieldService = inject(FieldService);

  readonly primaryButton = computed<LinkField | undefined>(
    () =>
      this.rendering().fields?.callToActionButtonLink as LinkField | undefined
  );
  readonly secondaryButton = computed<LinkField | undefined>(
    () =>
      this.rendering().fields?.callToActionSecondaryButtonLink as
        | LinkField
        | undefined
  );

  readonly showCallToActionButton = computed(
    () =>
      this.rendering().componentName ===
      TextSectionTemplateNames.WITH_CALL_TO_ACTION_BUTTON
  );
  readonly showAppStoreButtons = computed(
    () =>
      this.rendering().componentName ===
      TextSectionTemplateNames.WITH_APP_STORE_BUTTONS
  );

  readonly showPrimaryButton = computed(() =>
    this.#fieldService.showLinkField(this.primaryButton())
  );
  readonly showSecondaryButton = computed(() =>
    this.#fieldService.showLinkField(this.secondaryButton())
  );

  readonly #store = inject(Store);
  readonly appStoreButton = this.#store.selectSignal(getAppStoreButton);
  readonly googlePlayButton = this.#store.selectSignal(getGooglePlayButton);
}
